<template>
  <BaseModal
    :modelValue="modelValue"
    :size="'xl'"
    @update:modelValue="updateModelValue"
    @cancel="closeModal"
  >
    <template v-slot:title>
      {{ $t('Projects.New project popup.Title') }}
    </template>
    <form @submit.prevent="emitCreateProject" data-cy="project-creation-form" class="px-4 flex flex-col gap-6">
      <template v-if="sections[currentSection] === 'info'">
        <ProjectSectionInfo
          v-model:title="project.title"
          v-model:description="project.description"
          v-model:projectNumber="project.projectNumber"
          v-model:folderId="project.folderId"
          v-model:folderTitle="project.folderTitle"
          v-model:countryId="project.countryId"
          :folderOptions="folderOptions"
          :countryOptions="countryOptions"
          :validationErrors="validationErrors.info"
        />
      </template>
      <template v-if="sections[currentSection] === 'frameworks'">
        <ProjectSectionFrameworks
          :allowedFrameworks="allowedFrameworks"
          :selectedFramework="project.frameworkIds[0]"
          :validationErrors="validationErrors.frameworks"
          @selectedFrameworkUpdated="updateSelectedFrameworks"
        />
      </template>
      <template v-if="sections[currentSection] === 'goalCollections'">
        <ProjectSectionGoalCollection
          :goalCollections="projectGoalCollections"
          :selectedCollection="project.goalCollectionId"
          :validationErrors="validationErrors.goalCollections"
          @selectedCollectionUpdated="updateSelectedGoalCollection"
          @compareCollections="showCompareModal = true"
        />
      </template>
      <template v-if="sections[currentSection] === 'actionCollections'">
        <ProjectSectionCollections
          :allowedCollections="actionCollectionOptions"
          :selectedCollection="project.collectionId"
          :validationErrors="validationErrors.actionCollections"
          error-key="collectionId"
          :sectionTitle="$t('Projects.New project popup.Section_action_collections_title')"
          :sectionDescription="$t('Projects.New project popup.Section_action_collections_description')"
          @selectedCollectionUpdated="updateSelectedActionCollections"
        />
      </template>
      <template v-if="sections[currentSection] === 'questionCollections'">
        <ProjectSectionCollections
          :allowedCollections="questionCollectionOptions"
          :selectedCollection="project.questionCollectionId"
          :validationErrors="validationErrors.questionCollections"
          error-key="questionCollectionId"
          :sectionTitle="$t('Projects.New project popup.Section_question_collections_title')"
          :sectionDescription="$t('Projects.New project popup.Section_question_collections_description')"
          @selectedCollectionUpdated="updateSelectedQuestionCollections"
        />
      </template>
    </form>
    <BaseWizardActions
      :currentSectionIndex="currentSection"
      :maxSectionIndex="sections.length - 1"
      :disabled="isLoading"
      :labels="{
        previous: $t('Projects.New project popup.Form_action_previous'),
        next: $t('Projects.New project popup.Form_action_next'),
        create: $t('Projects.New project popup.Form_action_create'),
        cancel: $t('Projects.New project popup.Form_action_cancel'),
        skip: $t('Projects.New project popup.Form_action_skip'),
      }"
      @previous="previousSection"
      @next="nextSection"
      @create="handleCreate"
      @cancel="closeModal"
      @skip="nextSection"
    >
      <template v-slot:before-actions>
        <BaseButton
          v-if="sections[currentSection] === 'goalCollections'"
          primary
          :label="$t('Projects.New project popup.Compare_goal_collection_title')"
          @click="showCompareModal = true"
          :disabled="isLoading"
          class="btn"
        />
      </template>
      <template v-slot:loading>
        <div v-if="isLoading" class="flex items-center gap-2 text-sm text-gray-500">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          <span>{{ $t('Shared.loading') || 'Loading...' }}</span>
        </div>
      </template>
    </BaseWizardActions>
    <template v-slot:actions><div></div></template>
  </BaseModal>

  <ModalCompareGoalCollections
    v-model="showCompareModal"
    :collections="projectGoalCollections"
    :selected-collection-id="project.goalCollectionId"
    :project-frameworks="project.frameworkIds"
    @select="updateSelectedGoalCollection"
  />
</template>

<script>
import BaseModal from '@/components/shared/BaseModal'
import getAvailableCountries from '@/composables/countries/getAvailableCountries'
import findActionCollections from '@/composables/actionCollections/findActionCollections'
import findQuestionCollections from '@/composables/questionCollections/findQuestionCollections'
import findGoalCollections from '@/composables/goalCollections/findGoalCollections'
import ProjectSectionInfo from '@/components/projects/ProjectSectionInfo.vue'
import ProjectSectionFrameworks from '@/components/projects/ProjectSectionFrameworks.vue'
import ProjectSectionCollections from './ProjectSectionCollections.vue'
import ProjectSectionGoalCollection from './ProjectSectionGoalCollection.vue'
import ModalCompareGoalCollections from './ModalCompareGoalCollections.vue'
import BaseWizardActions from '@/components/shared/BaseWizardActions.vue'
import BaseButton from '@/components/shared/BaseButton.vue'
import { localeToCountryId } from '@/composables/utils'

export default {
  components: {
    BaseModal,
    ProjectSectionInfo,
    ProjectSectionFrameworks,
    ProjectSectionCollections,
    ProjectSectionGoalCollection,
    ModalCompareGoalCollections,
    BaseWizardActions,
    BaseButton,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['update:modelValue', 'create'],
  data () {
    return {
      showModal: false,
      showCompareModal: false,
      currentSection: 0,
      allowedFrameworks: [],
      isLoading: false,
      isSubmitting: false,
      isInitializing: false,
      validationErrors: {
        info: {},
        frameworks: {},
        goalCollections: {},
        actionCollections: {},
        questionCollections: {},
      },
      project: {
        title: '',
        description: '',
        projectNumber: null,
        folderId: null,
        folderTitle: '',
        countryId: null,
        frameworkIds: [],
        collectionId: null,
        questionCollectionId: null,
        goalCollectionId: null,
      },
      folderOptions: [
        { value: 'new', label: this.$t('Projects.New project popup.Label05') },
      ],
      countryOptions: [],
      addCollection: false,
      goalCollections: [],
    }
  },
  computed: {
    allFrameworks () {
      return this.$store.getters['frameworks/list']
    },
    sections () {
      const sections = ['info']
      if (this.allowedFrameworks.length > 1) {
        sections.push('frameworks')
      }
      if (this.goalCollectionOptions.length > 0) {
        sections.push('goalCollections')
      }
      if (this.actionCollectionOptions.length > 0) {
        sections.push('actionCollections')
      }
      if (this.questionCollectionOptions.length > 1) {
        sections.push('questionCollections')
      }
      console.log('sections', sections)
      return sections
    },
    selectedGoalCollectionsForCompare () {
      // Include the currently selected collection and one other for comparison
      const selected = this.goalCollections.find(c => c.id === this.project.goalCollectionId)

      // If nothing selected yet, show the first two collections
      if (!selected) {
        return this.goalCollectionOptions.slice(0, 2)
      }

      // Return the selected collection and another one for comparison
      const anotherCollection = this.goalCollectionOptions.find(c => c.id !== this.project.goalCollectionId)
      return [selected, anotherCollection].filter(Boolean)
    },
    actionCollectionOptions () {
      const options = this.$store.getters['action-collections/list']
        .filter(collection => (
          collection.isEnabled &&
          this.project.frameworkIds.includes(collection.frameworkId)
        ))
        .map(collection => ({
          label: collection.title,
          value: collection.id,
        }))
      return options
    },
    questionCollectionOptions () {
      console.log('questionCollectionOptions computed', this.$store.getters['question-collections/list'])
      const options = this.$store.getters['question-collections/list']
        .filter(collection => this.project.frameworkIds.includes(collection.frameworkId))
        .map(collection => ({
          label: collection.title,
          value: collection.id,
        }))
      return options
    },
    projectGoalCollections () {
      return this.goalCollections
        .filter(collection => this.project.frameworkIds.includes(collection.frameworkId))
    },
    goalCollectionOptions () {
      const options = this.projectGoalCollections
        .map(collection => ({
          label: collection.title,
          value: collection.id,
        }))
      console.log('goalCollectionOptions computed', options)
      return options
    },
    user () {
      return this.$store.getters['auth/user']
    },
  },
  methods: {
    updateModelValue (val) {
      this.$emit('update:modelValue', val)
    },
    closeModal () {
      this.$emit('update:modelValue', false)
      // close()
    },
    updateSelectedFrameworks (val) {
      console.log('updateSelectedFrameworks', val)
      this.project.frameworkIds = val ? [val] : []
    },
    updateSelectedActionCollections (val) {
      console.log('updateSelectedActionCollections', val)
      this.project.collectionId = val
    },
    updateSelectedQuestionCollections (val) {
      this.project.questionCollectionId = val
    },
    updateSelectedGoalCollection (val) {
      console.log('updateSelectedGoalCollection', val)
      this.project.goalCollectionId = val
    },
    validateCurrentSection () {
      const section = this.sections[this.currentSection]
      const errors = {}

      if (section === 'info') {
        // Title validation
        const trimmedTitle = this.project.title ? this.project.title.trim() : ''
        if (!trimmedTitle) {
          errors.title = this.$t('Projects.New project popup.Validation_title_required') || 'Title is required'
        } else if (trimmedTitle.length > 255) {
          errors.title = this.$t('Projects.New project popup.Validation_title_too_long') || 'Title must be 255 characters or less'
        }

        // Folder validation
        if (this.project.folderId === 'new') {
          const trimmedFolderTitle = this.project.folderTitle ? this.project.folderTitle.trim() : ''
          if (!trimmedFolderTitle) {
            errors.folderTitle = this.$t('Projects.New project popup.Validation_folder_title_required') || 'Folder title is required'
          } else if (trimmedFolderTitle.length > 255) {
            errors.folderTitle = this.$t('Projects.New project popup.Validation_folder_title_too_long') || 'Folder title must be 255 characters or less'
          }
        } else if (this.project.folderId !== null && this.project.folderId !== 'new') {
          // Validate that folderId exists in folderOptions
          const folderExists = this.folderOptions.some(
            option => option.value === this.project.folderId || Number(option.value) === Number(this.project.folderId),
          )
          if (!folderExists) {
            errors.folderId = this.$t('Projects.New project popup.Validation_folder_invalid') || 'Selected folder is invalid'
          }
        }

        // Country validation
        if (!this.project.countryId) {
          errors.countryId = this.$t('Projects.New project popup.Validation_country_required') || 'Country is required'
        } else {
          const countryExists = this.countryOptions.some(
            option => option.value === this.project.countryId || Number(option.value) === Number(this.project.countryId),
          )
          if (!countryExists) {
            errors.countryId = this.$t('Projects.New project popup.Validation_country_invalid') || 'Selected country is invalid'
          }
        }

        // Description length validation
        if (this.project.description && this.project.description.length > 2000) {
          errors.description = this.$t('Projects.New project popup.Validation_description_too_long') || 'Description must be 2000 characters or less'
        }

        // Project number validation
        if (this.project.projectNumber) {
          const trimmedProjectNumber = this.project.projectNumber.trim()
          if (trimmedProjectNumber.length > 50) {
            errors.projectNumber = this.$t('Projects.New project popup.Validation_project_number_too_long') || 'Project number must be 50 characters or less'
          }
        }
      } else if (section === 'frameworks') {
        // Framework validation
        if (!this.project.frameworkIds || this.project.frameworkIds.length === 0) {
          errors.frameworkIds = this.$t('Projects.New project popup.Validation_framework_required') || 'Framework is required'
        } else {
          const selectedFrameworkId = this.project.frameworkIds[0]
          const frameworkExists = this.allowedFrameworks.some(
            framework => framework.id === selectedFrameworkId || Number(framework.id) === Number(selectedFrameworkId),
          )
          if (!frameworkExists) {
            errors.frameworkIds = this.$t('Projects.New project popup.Validation_framework_invalid') || 'Selected framework is invalid or not allowed'
          } else {
            const selectedFramework = this.allowedFrameworks.find(
              framework => framework.id === selectedFrameworkId || Number(framework.id) === Number(selectedFrameworkId),
            )
            if (selectedFramework && !selectedFramework.isEnabled) {
              errors.frameworkIds = this.$t('Projects.New project popup.Validation_framework_disabled') || 'Selected framework is disabled'
            }
          }
        }
      } else if (section === 'actionCollections') {
        // Action collection validation (optional but if selected, must be valid)
        if (this.actionCollectionOptions.length > 0) {
          if (this.project.collectionId) {
            const collectionExists = this.actionCollectionOptions.some(
              option => option.value === this.project.collectionId || Number(option.value) === Number(this.project.collectionId),
            )
            if (!collectionExists) {
              errors.collectionId = this.$t('Projects.New project popup.Validation_action_collection_invalid') || 'Selected action collection is invalid'
            } else {
              // Validate that collection belongs to selected framework
              const selectedCollection = this.$store.getters['action-collections/list'].find(
                collection => collection.id === this.project.collectionId || Number(collection.id) === Number(this.project.collectionId),
              )
              if (selectedCollection && this.project.frameworkIds.length > 0) {
                const frameworkId = this.project.frameworkIds[0]
                if (selectedCollection.frameworkId !== frameworkId && Number(selectedCollection.frameworkId) !== Number(frameworkId)) {
                  errors.collectionId = this.$t('Projects.New project popup.Validation_action_collection_framework_mismatch') || 'Action collection does not match selected framework'
                }
              }
            }
          }
        }
      } else if (section === 'questionCollections') {
        // Question collection validation (optional but if selected, must be valid)
        if (this.questionCollectionOptions.length > 0) {
          if (this.project.questionCollectionId) {
            const collectionExists = this.questionCollectionOptions.some(
              option => option.value === this.project.questionCollectionId || Number(option.value) === Number(this.project.questionCollectionId),
            )
            if (!collectionExists) {
              errors.questionCollectionId = this.$t('Projects.New project popup.Validation_question_collection_invalid') || 'Selected question collection is invalid'
            } else {
              // Validate that collection belongs to selected framework
              const selectedCollection = this.$store.getters['question-collections/list'].find(
                collection => collection.id === this.project.questionCollectionId || Number(collection.id) === Number(this.project.questionCollectionId),
              )
              if (selectedCollection && this.project.frameworkIds.length > 0) {
                const frameworkId = this.project.frameworkIds[0]
                if (selectedCollection.frameworkId !== frameworkId && Number(selectedCollection.frameworkId) !== Number(frameworkId)) {
                  errors.questionCollectionId = this.$t('Projects.New project popup.Validation_question_collection_framework_mismatch') || 'Question collection does not match selected framework'
                }
              }
            }
          }
        }
      } else if (section === 'goalCollections') {
        // Goal collection validation (optional but if selected, must be valid)
        if (this.goalCollectionOptions.length > 0) {
          if (this.project.goalCollectionId) {
            const collectionExists = this.goalCollectionOptions.some(
              option => option.value === this.project.goalCollectionId || Number(option.value) === Number(this.project.goalCollectionId),
            )
            if (!collectionExists) {
              errors.goalCollectionId = this.$t('Projects.New project popup.Validation_goal_collection_invalid') || 'Selected goal collection is invalid'
            } else {
              // Validate that collection belongs to selected framework
              const selectedCollection = this.projectGoalCollections.find(
                collection => collection.id === this.project.goalCollectionId || Number(collection.id) === Number(this.project.goalCollectionId),
              )
              if (selectedCollection && this.project.frameworkIds.length > 0) {
                const frameworkId = this.project.frameworkIds[0]
                if (!selectedCollection.frameworkId || (selectedCollection.frameworkId !== frameworkId && Number(selectedCollection.frameworkId) !== Number(frameworkId))) {
                  errors.goalCollectionId = this.$t('Projects.New project popup.Validation_goal_collection_framework_mismatch') || 'Goal collection does not match selected framework'
                }
              }
            }
          }
        }
      }

      this.validationErrors[section] = errors
      return Object.keys(errors).length === 0
    },
    sanitizeProjectData () {
      // Trim all string fields
      const sanitized = { ...this.project }
      if (sanitized.title) {
        sanitized.title = sanitized.title.trim()
      }
      if (sanitized.description) {
        sanitized.description = sanitized.description.trim()
      }
      if (sanitized.folderTitle) {
        sanitized.folderTitle = sanitized.folderTitle.trim()
      }
      if (sanitized.projectNumber) {
        sanitized.projectNumber = sanitized.projectNumber.trim()
      }
      return sanitized
    },
    previousSection () {
      if (this.isLoading) {
        return
      }
      if (this.currentSection > 0) {
        this.currentSection--
        // Clear validation errors when going back
        const section = this.sections[this.currentSection]
        if (this.validationErrors[section]) {
          this.validationErrors[section] = {}
        }
      }
    },
    async nextSection () {
      if (this.isLoading) {
        return
      }

      // Validate current section before proceeding
      if (!this.validateCurrentSection()) {
        return
      }

      if (this.currentSection < this.sections.length - 1) {
        this.currentSection++
        // Clear validation errors for the new section
        const section = this.sections[this.currentSection]
        if (this.validationErrors[section]) {
          this.validationErrors[section] = {}
        }
      }
    },
    handleCreate () {
      if (this.isSubmitting || this.isLoading) {
        return
      }

      // Validate all sections before creating
      const originalSection = this.currentSection
      let isValid = true

      // Initialize validation errors for all sections
      this.sections.forEach(section => {
        if (!this.validationErrors[section]) {
          this.validationErrors[section] = {}
        }
      })

      // Validate all sections
      for (let i = 0; i < this.sections.length; i++) {
        this.currentSection = i
        if (!this.validateCurrentSection()) {
          isValid = false
          break
        }
      }

      // Restore original section
      this.currentSection = originalSection

      if (!isValid) {
        // Navigate to first invalid section
        for (let i = 0; i < this.sections.length; i++) {
          const section = this.sections[i]
          if (this.validationErrors[section] && Object.keys(this.validationErrors[section]).length > 0) {
            this.currentSection = i
            break
          }
        }
        return
      }

      // Prevent duplicate submissions
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true

      // Sanitize data before emitting
      const sanitizedProject = this.sanitizeProjectData()
      this.$emit('create', sanitizedProject)
    },
  },
  watch: {
    'project.frameworkIds': async function (newFrameworkIds, oldFrameworkIds) {
      // Skip if modal is not open or during initial setup
      if (!this.modelValue || this.isInitializing) {
        return
      }
      this.isLoading = true
      await findActionCollections(false, this.project.countryId, newFrameworkIds)
      this.isLoading = false
    },
    'project.countryId': async function (newCountryId, oldCountryId) {
      // Skip if modal is not open or during initial setup
      if (!this.modelValue || this.isInitializing) {
        return
      }
      this.isLoading = true
      try {
        if (newCountryId !== oldCountryId) {
          this.collectionId = null
          // This just updates the store
          await findActionCollections(false, newCountryId, this.project.frameworkIds)
          // This gets collections and adds goal-collections-sub-categories and datasets (for visualizations)
          this.goalCollections = await findGoalCollections(newCountryId, null, 'project.contryId watcher')
        }

        // Filter frameworks by country
        const organizationFrameworks = await this.$store.dispatch('organizations-frameworks/find', {
          query: {
            organizationId: this.$store.getters['auth/user'].organizationId,
          },
        })
        console.log('ORGANIZATION FRAMEWORKS', organizationFrameworks)
        console.log('FRAMEWORKS', this.$store.getters['frameworks/list'])
        const user = this.$store.getters['auth/user']
        const userFrameworkIds = Array.isArray(user.frameworkIds) ? user.frameworkIds : []
        const selectedCountryId = newCountryId == null ? null : Number(newCountryId)

        this.allowedFrameworks = this.$store.getters['frameworks/list']
          .filter(framework => {
            if (!framework.isEnabled) {
              return false
            }

            const isAllowedForUser = organizationFrameworks.some(orgFramework => orgFramework.frameworkId === framework.id) ||
              userFrameworkIds.includes(framework.id)

            if (!isAllowedForUser) {
              return false
            }

            const frameworkCountryId = framework.countryId == null ? null : Number(framework.countryId)

            if (selectedCountryId === null) {
              return frameworkCountryId === null
            }

            return frameworkCountryId === null || frameworkCountryId === selectedCountryId
          })

        const allowedFrameworkIds = this.allowedFrameworks.map(framework => framework.id)
        if (!allowedFrameworkIds.includes(this.project.frameworkIds[0])) {
          this.project.frameworkIds = this.allowedFrameworks.length > 0 ? [this.allowedFrameworks[0].id] : []
        }
      } catch (error) {
        console.error('Error loading data for country:', error)
      } finally {
        this.isLoading = false
      }
    },
    async modelValue (newValue) {
      if (newValue) {
        this.isLoading = true
        this.isSubmitting = false
        this.isInitializing = true // Prevent watchers from triggering during setup
        this.validationErrors = {
          info: {},
          frameworks: {},
          goalCollections: {},
          actionCollections: {},
          questionCollections: {},
        }
        try {
          await this.$store.dispatch('compass-folder/find')

          const countries = await getAvailableCountries()
          // const noCountryLabel = this.$t('Projects.New project popup.country_select_no_country')
          this.countryOptions = [
            // { value: null, label: noCountryLabel },
            ...countries.map(country => ({ label: country.native, value: country.id })),
          ]

          const compassFolders = this.$store.getters['compass-folder/list']
          const newFolderLabel = this.$t('Projects.New project popup.Label05')
          this.folderOptions = [
            { value: 'new', label: newFolderLabel },
            ...compassFolders.map(folder => ({ value: folder.id, label: folder.title })),
          ]

          this.allowedQuestionCollections = this.$store.getters['question-collections/list']
          this.allowedActionCollections = this.$store.getters['action-collections/list']

          const initialCountryId = localeToCountryId(this.$i18n.locale)

          // Filter frameworks by country first
          const organizationFrameworks = await this.$store.dispatch('organizations-frameworks/find', {
            query: {
              organizationId: this.$store.getters['auth/user'].organizationId,
            },
          })
          const user = this.$store.getters['auth/user']
          const userFrameworkIds = Array.isArray(user.frameworkIds) ? user.frameworkIds : []
          const selectedCountryId = initialCountryId == null ? null : Number(initialCountryId)

          this.allowedFrameworks = this.$store.getters['frameworks/list']
            .filter(framework => {
              if (!framework.isEnabled) {
                return false
              }
              const isAllowedForUser = organizationFrameworks.some(orgFramework => orgFramework.frameworkId === framework.id) ||
                userFrameworkIds.includes(framework.id)
              if (!isAllowedForUser) {
                return false
              }
              const frameworkCountryId = framework.countryId == null ? null : Number(framework.countryId)
              if (selectedCountryId === null) {
                return frameworkCountryId === null
              }
              return frameworkCountryId === null || frameworkCountryId === selectedCountryId
            })

          const initialFrameworkIds = this.allowedFrameworks[0] ? [this.allowedFrameworks[0].id] : []

          // Set project data
          this.project = {
            title: '',
            description: '',
            projectNumber: null,
            folderId: null,
            folderTitle: '',
            countryId: initialCountryId,
            collectionId: null,
            questionCollectionId: null,
            goalCollectionId: null,
            frameworkIds: initialFrameworkIds,
          }

          // Make a single consolidated call for collections
          await Promise.all([
            findActionCollections(false, initialCountryId, initialFrameworkIds),
            findQuestionCollections(),
            findGoalCollections(initialCountryId, null, 'modalAddProject, modelValue watcher').then(collections => {
              this.goalCollections = collections
            }),
          ])

          // Set default collections if available
          if (this.questionCollectionOptions.length > 0) {
            this.project.questionCollectionId = this.questionCollectionOptions[0].value
          }
          if (this.goalCollectionOptions.length > 0) {
            this.project.goalCollectionId = this.goalCollectionOptions[0].value
          }

          this.currentSection = 0
        } catch (error) {
          console.error('Error initializing modal:', error)
        } finally {
          this.isInitializing = false // Re-enable watchers
          this.isLoading = false
        }
      } else {
        // Reset state when modal closes
        this.isSubmitting = false
        this.isLoading = false
        this.validationErrors = {
          info: {},
          frameworks: {},
          goalCollections: {},
          actionCollections: {},
          questionCollections: {},
        }
      }
    },
    addCollection (newValue) {
      if (newValue && this.actionCollectionOptions.length) {
        this.project.collectionId = this.actionCollectionOptions[0].value
      } else {
        this.project.collectionId = null
      }
    },
    questionCollectionOptions: {
      handler (newValue) {
        console.log('questionCollectionOptions handler', newValue)
        if (newValue.length > 0) {
          this.project.questionCollectionId = newValue[0].value
        }
      },
      immediate: true,
      deep: true,
    },
  },
  async mounted () {
    // Collections are loaded when modal opens via modelValue watcher
    // No need to load them on mount
  },
}
</script>

<style scoped>
button:not(:last-child) {
  margin-right: 1rem;
}
</style>
